import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { PharmacIcon, PharmacIconProps } from '@pharmac/ui'
import React, { PropsWithChildren, useMemo, useState } from 'react'
import { NavLink, To } from 'react-router-dom'

export type NavigationMenuItemProps = {
  label?: string
  onClick?: () => void
  navigateTo?: To
  isActive?: boolean
  icon?: PharmacIconProps['icon']
  children?: NavigationMenuItemProps[]
  dividerBefore?: boolean
  dividerAfter?: boolean
}

export type NavigationMenuItem = Omit<NavigationMenuItemProps, 'children'> & {
  id: string
  children?: NavigationMenuItem[]
}

type MenuItemPropsWithChildren = NavigationMenuItemProps &
  Required<Pick<NavigationMenuItemProps, 'children'>>

export type MenuProps = {
  menu: NavigationMenuItemProps[]
  clickCallback?: () => void
  xl?: boolean
}

const ItemInnerStyles = css`
  width: 100%;

  background-color: transparent;

  outline: 0;
  border: 0;

  color: var(--color-primary);

  overflow: hidden;

  &:hover {
    cursor: pointer;
  }

  a,
  button {
    text-decoration: none;
    outline: 0;
    border: 0;
    width: 100%;

    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
  }
`

const topLevelFont = (p: { xl?: boolean }) => css`
  font-size: ${p.xl ? 20 : 16}px;
  font-weight: ${p.xl ? 800 : 700};
  font-family: var(--font-family-secondary);
`

const subLevelFont = (p: { xl?: boolean }) => css`
  font-size: ${p.xl ? 18 : 16}px;
  font-weight: ${p.xl ? 700 : 600};
  font-family: var(--font-family-secondary);
`

const MenuListRoot = styled.div<{ xl?: boolean }>`
  position: sticky;
  top: 85px;
  left: 0;

  z-index: 225;

  ul.enmenu {
    list-style: none;
    width: 100%;

    li.enmenu___item {
      ${ItemInnerStyles}
      padding: ${(p) =>
        p.xl
          ? 'var(--spacing-medium) var(--spacing-small)'
          : 'var(--spacing-tiny) var(--spacing-small)'};

      &.enmenu___item--divider {
        padding: var(--spacing-medium) 0;
        &::after {
          display: block;
          content: '';
          background-color: var(--color-purple-light);
          height: 2px;
        }
      }

      .enmenu___item-button {
        padding: 0 var(--spacing-small);
        width: max-content;

        &--active {
          .enmenu___item-label {
            background-color: var(--color-primary);
            color: var(--color-white) !important;
          }
        }

        &--active,
        &:hover {
          .enmenu___item-label {
            background-color: var(--color-primary);
            color: var(--color-white);
            border-radius: 14px;
          }
        }

        .enmenu___item-icon {
          width: 30px;

          svg {
            color: var(--color-primary);
            height: 22px;
            width: 22px;
            transform: translateY(1px);
          }
        }

        .enmenu___item-label {
          padding-left: var(--spacing-small);
          padding-right: var(--spacing-small);
          color: var(--color-primary);
          ${topLevelFont};
        }

        .enmenu___item-caret {
          color: var(--color-primary);
          padding-left: var(--spacing-small);
          padding-right: var(--spacing-tiny);

          svg {
            transition: var(--transition-cubic-bezier);
          }
        }

        .enmenu___item-caret--rotate {
          svg {
            transform: rotate(180deg);
          }
        }
      }

      .enmenu___dropdown {
        transition: all 0.1s var(--ease-cubic-bezier);

        &--open {
          max-height: 5000px;
          opacity: 1;
          padding: var(--spacing-small) 0;
        }

        &--closed {
          max-height: 0px;
          opacity: 0;
          padding: 0 0;
        }

        .enmenu___item {
          width: 100%;
          padding-left: 30px;

          &:not(:last-of-type) {
            margin-bottom: var(--spacing-tiny);
          }

          .enmenu___item-button {
            width: 100%;
            .enmenu___item-icon {
              display: none;
            }
            .enmenu___item-label {
              ${subLevelFont};
              white-space: wrap;
            }
          }
        }
      }
    }
  }
`

const mapMenuItem = (item: NavigationMenuItemProps): NavigationMenuItem => {
  // eslint-disable-next-line
  const { children, ...props } = item

  return {
    id: crypto.randomUUID(),
    children: hasSubmenu(item) ? item.children.map(mapMenuItem) : undefined,
    ...props,
  }
}

export const hasSubmenu = (a: NavigationMenuItemProps): a is MenuItemPropsWithChildren => {
  return !!a.children && Array.isArray(a.children) && a.children.length > 0
}

const ItemButton = ({
  onClick,
  navigateTo,
  children,
  isActive,
}: PropsWithChildren & Omit<NavigationMenuItemProps, 'children' | 'label'>) => {
  if (!!navigateTo) {
    return (
      <NavLink
        className={classNamer(isActive, 'enmenu___item-button', 'enmenu___item-button--active')}
        onClick={onClick}
        to={navigateTo}
      >
        {children}
      </NavLink>
    )
  }

  return (
    <button
      className={classNamer(isActive, 'enmenu___item-button', 'enmenu___item-button--active')}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

type NavigationMenuItemComponentProps = {
  item: NavigationMenuItem
  toggle: (id: string) => void
  isOpen: (id: string) => boolean
  handleClick: (item: NavigationMenuItem) => void
}

const classNamer = (condition: any, base: string, truthy: string, falsy?: string) => {
  return [base, !!condition && truthy, !condition && !!falsy && falsy].filter(Boolean).join(' ')
}

export const NavigationMenuItemComponent = ({
  item,
  toggle,
  handleClick,
  isOpen,
}: NavigationMenuItemComponentProps) => {
  if (hasSubmenu(item)) {
    return (
      <React.Fragment key={item.id}>
        {!!item.dividerBefore && <li className="enmenu___item enmenu___item--divider" />}
        <li className="enmenu___item">
          <button
            className={classNamer(
              item.isActive,
              'enmenu___item-button',
              'enmenu___item-button--active',
              ''
            )}
            onClick={() => toggle(item.id)}
          >
            <div className="enmenu___item-icon">
              {item.icon && <PharmacIcon icon={item.icon} />}
            </div>
            <div className="enmenu___item-label">{item.label}</div>
            <div
              className={['enmenu___item-caret', isOpen(item.id) && 'enmenu___item-caret--rotate']
                .filter(Boolean)
                .join(' ')}
            >
              <PharmacIcon icon="chevronDown" />
            </div>
          </button>
          <ul
            className={classNamer(
              isOpen(item.id),
              'enmenu enmenu___dropdown',
              'enmenu___dropdown--open',
              'enmenu___dropdown--closed'
            )}
          >
            {item.children.map((b) => (
              <NavigationMenuItemComponent
                key={b.id}
                toggle={toggle}
                handleClick={handleClick}
                isOpen={isOpen}
                item={b}
              />
            ))}
          </ul>
        </li>
        {!!item.dividerAfter && <li className="enmenu___item enmenu___item--divider" />}
      </React.Fragment>
    )
  }

  return (
    <React.Fragment key={item.id}>
      {!!item.dividerBefore && <li className="enmenu___item enmenu___item--divider" />}
      <li
        className="enmenu___item"
        key={item.id}
      >
        <ItemButton
          onClick={() => handleClick(item)}
          navigateTo={item.navigateTo}
          isActive={item.isActive}
        >
          <div className="enmenu___item-icon">{item.icon && <PharmacIcon icon={item.icon} />}</div>
          <div className="enmenu___item-label">{item.label}</div>
        </ItemButton>
      </li>
      {!!item.dividerAfter && <li className="enmenu___item enmenu___item--divider" />}
    </React.Fragment>
  )
}

export const NavigationMenu = ({ menu, clickCallback, xl }: MenuProps) => {
  const data: NavigationMenuItem[] = useMemo(() => menu.map(mapMenuItem), [menu])

  const [state, setState] = useState<string[]>([])

  const isOpen = (id: string) => state.includes(id)

  const toggle = (id: string) => {
    setState((s) => (isOpen(id) ? s.filter((b) => b !== id) : s.concat(id)))
  }

  const handleClick = (a: NavigationMenuItemProps) => {
    if (typeof a.onClick === 'function') a.onClick()

    if (typeof clickCallback === 'function') clickCallback()
  }

  return (
    <>
      <MenuListRoot
        xl={!!xl}
        className="enmenu-container"
      >
        <ul className="enmenu">
          {data.map((item) => (
            <NavigationMenuItemComponent
              item={item}
              key={item.id}
              handleClick={handleClick}
              toggle={toggle}
              isOpen={isOpen}
            />
          ))}
        </ul>
      </MenuListRoot>
    </>
  )
}
